import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { SURFACE_COLOR_FIELD_AI_SCHEMA, SurfaceColorField } from '../fields/widgets/surface-color-field';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  resolveBackgroundColorWithOpacity,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import { createSpacingFields } from '../fields/spacing-fields';
import { CmsRichTextMenu, CmsTextRichTextMenu, cmsRichTextSelector } from '../fields/widgets/richtext-menu';
import { toggleField } from '../fields/widgets/toggle-field';
import { interpolateHtml } from '../interpolate-html';
import { getCmsVariables, interpolate } from '../interpolation';
import { coerceTextHeadingsToParagraphs, sanitizeCmsHtml } from '../sanitize-html';
import {
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FAQ_ANSWER_INSET,
  FAQ_DIVIDER_WIDTH,
  FAQ_HEADING_GAP,
  FAQ_QUESTION_FONT_WEIGHT,
  FAQ_QUESTION_GAP,
  FAQ_QUESTION_INSET,
  FONT_ROLES,
  HEADING_PRESETS,
  isTextColorRole,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { BoxSpacing, FontRole, MaxWidthOption } from '../fields/tokens';

function resolveFaqAccentColor(value: string, fallback: string): string {
  if (isTextColorRole(value)) return resolveTextColor(value);
  return /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value) ? value : fallback;
}

/**
 * Disclosure glyphs, swapped by CSS content so the open/closed state needs no JS.
 * `details[open]` drives the change.
 *
 * The caret rotates a single glyph rather than swapping U+2304 (⌄) for U+2303 (⌃). Those are
 * independently designed codepoints with no guarantee of matching optical size, and U+2304 has
 * patchy font coverage, so on some platforms it falls back to a different font entirely — which
 * rendered the two states at visibly different sizes. Rotating one glyph makes the two states
 * identical by construction, on every font and platform.
 *
 * Plus/minus still swap: U+002B and U+2212 are ubiquitous and metrically consistent.
 */
const DISCLOSURE_ICONS = {
  caret: { collapsed: "'⌃'", expanded: "'⌃'", collapsedTransform: 'rotate(180deg)', expandedTransform: 'none' },
  plus: { collapsed: "'+'", expanded: "'−'", collapsedTransform: 'none', expandedTransform: 'none' },
} as const;

type DisclosureIcon = keyof typeof DISCLOSURE_ICONS;

/**
 * Semantic heading levels available for the FAQ section heading.
 * H1 is included for standalone FAQ pages where the block's heading legitimately
 * serves as the page's top-level heading.
 */
const FAQ_HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

type FaqHeadingLevel = typeof FAQ_HEADING_LEVELS[number];

/**
 * Named icon sizes for the disclosure glyph. Drives the font-size of the ::after pseudo-element
 * as a relative (em) value so the glyph scales with the inherited question font-size token rather
 * than being severed from it.
 *
 * medium = 1em reproduces the pre-change behavior exactly: the ::after previously had no
 * font-size override and therefore inherited the summary's `font-size: var(--cms-faq-question-font-size, …)`
 * directly — which is exactly what 1em does. A merchant theme overriding that token still scales
 * the glyph in lockstep.
 *
 * small = 0.85em (~15–16px at 18px base), large = 1.35em (~24px at 18px base).
 */
const FAQ_ICON_SIZES = {
  small: '0.85em',
  medium: '1em',
  large: '1.35em',
} as const;

type FaqIconSize = keyof typeof FAQ_ICON_SIZES;

type TextAlign = 'center' | 'left';

function isLiteralTrue(value: unknown): value is true {
  return value === true;
}

type FaqRow = {
  question: string;
  answer: string;
  openByDefault: boolean;
};

export type FaqProps = {
  heading: string;
  /** Semantic heading level for the section heading (H1–H6). Default h3. */
  level: FaqHeadingLevel;
  rows: FaqRow[];
  icon: DisclosureIcon;
  /** Size of the disclosure glyph. */
  iconSize: FaqIconSize;
  dividers: boolean;
  /** Alignment of the block within its container and of heading/question/answer text. */
  align: TextAlign;
  font: FontRole;
  /** Section heading color. Falls back to `color` on pages saved before it was separable. */
  headingColor?: string;
  /**
   * Question color. Keeps the key `color` rather than renaming to `questionColor` so pages saved
   * before heading/answer were separable need no migration.
   */
  color: string;
  answerColor: string;
  iconColor: string;
  dividerColor: string;
  background: string;
  backgroundOpacity: number;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/**
 * Built on native `<details>`/`<summary>` rather than wrapping an existing accordion.
 * The shared MUI accordion is checkout-specific (its theme override pins `maxWidth: 390`
 * and it uses `AccordionActions` for content), and `CollapsibleSection` hardcodes an
 * uppercase 13px form-label heading — neither can carry the token vocabulary for the
 * question text. Native disclosure also gives keyboard and screen-reader support, and
 * open-by-default, without managed state.
 */
const Wrapper = styled.section`
  background: var(--cms-faq-background, ${SURFACE_COLOR_ROLES.none});
  font-family: var(--cms-faq-font-family, ${FONT_ROLES.body});
  color: var(--cms-faq-color, ${TEXT_COLOR_ROLES.text});
  width: 100%;
  max-width: var(--cms-faq-max-width, ${MAX_WIDTHS.none});
  box-sizing: border-box;
  margin-left: var(--cms-faq-margin-inline, 0px);
  margin-right: var(--cms-faq-margin-inline, 0px);
  padding: var(--cms-faq-padding, 0px);
  text-align: var(--cms-faq-text-align, left);
`;

const SectionHeading = styled.h3`
  margin: 0 0 ${FAQ_HEADING_GAP};
  font-size: var(--cms-faq-heading-font-size, ${HEADING_PRESETS.h3.fontSize});
  font-weight: var(--cms-faq-heading-font-weight, ${HEADING_PRESETS.h3.fontWeight});
  /* Falls back to the inherited question color, which is what pre-split pages expect. */
  color: var(--cms-faq-heading-color, inherit);
`;

const Row = styled.details`
  border-top: var(--cms-faq-divider, none);

  &:last-of-type {
    border-bottom: var(--cms-faq-divider, none);
  }

  > summary {
    display: flex;
    align-items: center;
    justify-content: var(--cms-faq-summary-justify, space-between);
    gap: ${FAQ_QUESTION_GAP};
    padding: ${FAQ_QUESTION_INSET};
    cursor: pointer;
    font-size: var(--cms-faq-question-font-size, ${BODY_TEXT_SIZES.xl});
    font-weight: ${FAQ_QUESTION_FONT_WEIGHT};
    list-style: none;

    &:focus-visible {
      outline: var(--dcm-cms-faq-focus-outline, 3px solid var(--puck-color-focus-ring, #0b99e6));
      outline-offset: var(--dcm-cms-faq-focus-outline-offset, -3px);
    }
  }

  /* Let long tokens shrink and wrap while the disclosure glyph keeps its own space. */
  > summary > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  /* Suppress the native marker so the themeable glyph below is the only one. */
  > summary::-webkit-details-marker {
    display: none;
  }

  > summary::after {
    content: var(--cms-faq-icon-collapsed, "'+'");
    flex: none;
    /* inline-block so the caret's rotation applies; transforms are ignored on inline boxes. */
    display: inline-block;
    transform: var(--cms-faq-icon-collapsed-transform, none);
    color: var(--dcm-cms-faq-icon-color, var(--cms-faq-icon-color, ${TEXT_COLOR_ROLES.muted}));
    font-size: var(--cms-faq-icon-size, ${FAQ_ICON_SIZES.medium});
  }

  &[open] > summary::after {
    content: var(--cms-faq-icon-expanded, "'−'");
    transform: var(--cms-faq-icon-expanded-transform, none);
  }
`;

const Answer = styled.div`
  padding: ${FAQ_ANSWER_INSET};
  font-size: var(--cms-faq-answer-font-size, ${BODY_TEXT_SIZES.lg});
  line-height: var(--cms-faq-line-height, ${BODY_LINE_HEIGHT});
  color: var(--cms-faq-answer-color, ${TEXT_COLOR_ROLES.secondary});

  > :first-child,
  .ProseMirror > :first-child {
    margin-top: 0;
  }

  > :last-child,
  .ProseMirror > :last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--dcm-cms-faq-link-color, var(--dcm-color-link, currentColor));
    text-decoration: underline;
  }
`;

const RICHTEXT_BLOCK_PATTERN = /^\s*<(?:blockquote|h[1-6]|hr|ol|p|pre|ul)\b[\s\S]*>/i;

function preservePlainTextNewlines(text: string): string {
  if (RICHTEXT_BLOCK_PATTERN.test(text)) {
    return text;
  }
  const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escapedText
    .split(/\r?\n\r?\n/)
    .map((paragraph) => `<p>${paragraph.replace(/\r?\n/g, '<br>')}</p>`)
    .join('');
}

export const faqConfig: ComponentConfig<FaqProps> = {
  label: 'FAQ',
  ai: {
    instructions:
      'A list of frequently asked questions as expandable rows. Write a real question in each `question` and a complete answer in each `answer`. Keep answers to a short paragraph.',
  },
  fields: {
    heading: { type: 'text', label: 'Heading' },
    level: {
      type: 'select',
      label: 'Heading level',
      options: FAQ_HEADING_LEVELS.map((l) => ({ label: l.toUpperCase(), value: l })),
      ai: { schema: { type: 'string', enum: [...FAQ_HEADING_LEVELS] } },
    },
    rows: {
      type: 'array',
      label: 'Questions',
      getItemSummary: (row) => row.question || 'New question',
      defaultItemProps: { question: 'New question', answer: '', openByDefault: false },
      arrayFields: {
        question: { type: 'text', label: 'Question' },
        answer: {
          type: 'richtext',
          label: 'Answer',
          renderMenu: CmsTextRichTextMenu,
          renderInlineMenu: CmsRichTextMenu,
          tiptap: { selector: cmsRichTextSelector },
          options: {
            heading: false,
            link: { openOnClick: false },
          },
        },
        openByDefault: toggleField('Open by default'),
      },
      ai: {
        instructions: 'Between three and eight rows reads best. Only open the first row by default, if any.',
      },
    },
    background: {
      type: 'custom',
      label: 'Background',
      render: SurfaceColorField,
      ai: {
        schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
        instructions:
          "Optional fill behind the FAQ. Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: { schema: { type: 'string', enum: ['heading', 'body'] } },
    },
    headingColor: {
      type: 'custom',
      label: 'Heading color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Color of the section heading. A theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value.",
        },
      },
    },
    color: {
      type: 'custom',
      label: 'Question color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Color of the question rows. A theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value.",
        },
      },
    },
    answerColor: {
      type: 'custom',
      label: 'Answer color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Color of the answer text. Defaults to the 'secondary' role so answers read as a lower tier than questions.",
        },
      },
    },
    iconColor: {
      type: 'custom',
      label: 'Icon color',
      render: ColorRoleField,
      ai: {
        schema: { type: 'string', description: 'A theme text color role or a custom hex color for the expand icon.' },
      },
    },
    dividers: toggleField('Show dividers', undefined, undefined, { defaultOn: true }),
    dividerColor: {
      type: 'custom',
      label: 'Divider color',
      render: ColorRoleField,
      ai: {
        schema: { type: 'string', description: 'A theme text color role or a custom hex color for the row dividers.' },
      },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Max width',
      options: [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'Full', value: 'none' },
      ],
      ai: { schema: { type: 'string', enum: ['narrow', 'normal', 'none'] } },
    },
    icon: {
      type: 'radio',
      label: 'Expand icon',
      options: [
        { label: 'Caret', value: 'caret' },
        { label: 'Plus', value: 'plus' },
      ],
      ai: { schema: { type: 'string', enum: ['caret', 'plus'] } },
    },
    iconSize: {
      type: 'radio',
      label: 'Expand icon size',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
      ],
      ai: { schema: { type: 'string', enum: ['small', 'medium', 'large'] } },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
  },
  resolveFields: (data, { fields }) => resolveBackgroundOpacityFields(data.props.background, fields),
  resolveData: ({ props }) => {
    if (!Array.isArray(props.rows)) {
      return { props };
    }

    const rows = props.rows.map((row) => {
      const answer = row.answer as unknown;
      if (typeof answer !== 'string' || answer === '' || RICHTEXT_BLOCK_PATTERN.test(answer)) {
        return row;
      }
      return { ...row, answer: preservePlainTextNewlines(answer) };
    });
    const changed = rows.some((row, index) => row !== props.rows[index]);

    return changed ? { props: { ...props, rows } } : { props };
  },
  defaultProps: {
    heading: 'Frequently asked questions',
    level: 'h3',
    rows: [
      { question: 'What is your return policy?', answer: '', openByDefault: false },
      { question: 'How long does delivery take?', answer: '', openByDefault: false },
    ],
    icon: 'plus',
    iconSize: 'medium',
    dividers: true,
    align: 'left',
    font: 'body',
    headingColor: 'text',
    color: 'text',
    answerColor: 'secondary',
    iconColor: 'muted',
    dividerColor: 'secondary',
    background: 'none',
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    maxWidth: 'normal',
    padding: NO_SPACING,
  },
  render: ({
    heading = '',
    level = 'h3',
    rows,
    icon = 'plus',
    iconSize = 'medium',
    dividers = true,
    align = 'left',
    font = 'body',
    headingColor,
    color = 'text',
    answerColor = 'secondary',
    iconColor = 'muted',
    dividerColor = 'secondary',
    background = 'none',
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    maxWidth = 'normal',
    padding,
    puck,
  }) => {
    const glyphs = lookup(DISCLOSURE_ICONS, icon, DISCLOSURE_ICONS.plus);
    const items = Array.isArray(rows) ? rows : [];
    const sectionLevel = FAQ_HEADING_LEVELS.includes(level) ? level : 'h3';
    const iconSizePx = lookup(FAQ_ICON_SIZES, iconSize, FAQ_ICON_SIZES.medium);
    const headingSizing = HEADING_PRESETS[sectionLevel];
    const cmsVariables = getCmsVariables(puck);
    const displayHeading = interpolate(heading, cmsVariables);

    const textAlign = lookup({ left: 'left', center: 'center' } as const, align, 'left');
    // The canonical contract is boolean, but persisted input is untrusted at runtime. Only an explicit
    // false hides dividers; omitted or malformed values fall back to the visible default.
    const storedDividers: unknown = dividers;
    const showDividers = storedDividers !== false;

    return (
      <Wrapper
        style={
          {
            '--cms-faq-background': resolveBackgroundColorWithOpacity(background, backgroundOpacity),
            '--cms-faq-font-family': lookup(FONT_ROLES, font, FONT_ROLES.body),
            '--cms-faq-color': resolveTextColor(color),
            // Missing or malformed stored data must not make the heading unreadable.
            '--cms-faq-heading-color': resolveTextColor(headingColor ?? color),
            '--cms-faq-heading-font-size': headingSizing.fontSize,
            '--cms-faq-heading-font-weight': headingSizing.fontWeight,
            '--cms-faq-answer-color': resolveTextColor(answerColor),
            '--cms-faq-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.normal),
            '--cms-faq-padding': spacingToCss(padding),
            '--cms-faq-text-align': textAlign,
            '--cms-faq-margin-inline': textAlign === 'center' ? 'auto' : '0px',
            // When centered the icon stays in the flex row next to the text (justify-content: center
            // centers the whole row together). This is preferable to keeping space-between, which
            // would pin the icon to the right edge while the text centers — a mismatched layout.
            '--cms-faq-summary-justify': textAlign === 'center' ? 'center' : 'space-between',
            '--cms-faq-icon-collapsed': glyphs.collapsed,
            '--cms-faq-icon-expanded': glyphs.expanded,
            '--cms-faq-icon-collapsed-transform': glyphs.collapsedTransform,
            '--cms-faq-icon-expanded-transform': glyphs.expandedTransform,
            '--cms-faq-icon-size': iconSizePx,
            '--cms-faq-icon-color': resolveFaqAccentColor(iconColor, TEXT_COLOR_ROLES.muted),
            '--cms-faq-divider': showDividers
              ? `${FAQ_DIVIDER_WIDTH} solid var(--dcm-cms-faq-divider-color, ${resolveFaqAccentColor(
                  dividerColor,
                  TEXT_COLOR_ROLES.secondary
                )})`
              : 'none',
          } as React.CSSProperties
        }
      >
        {displayHeading && <SectionHeading as={sectionLevel}>{displayHeading}</SectionHeading>}
        {items.map((row, index) => {
          const displayQuestion = interpolate(row.question, cmsVariables);
          const answerContent = row.answer as unknown;
          let renderedAnswer: React.ReactNode = null;

          if (typeof answerContent !== 'string') {
            if (answerContent) {
              renderedAnswer = <Answer>{answerContent as React.ReactNode}</Answer>;
            }
          } else if (answerContent) {
            const normalizedAnswer = preservePlainTextNewlines(answerContent);
            const interpolatedHtml = interpolateHtml(normalizedAnswer, cmsVariables);
            const sanitizedHtml = sanitizeCmsHtml(coerceTextHeadingsToParagraphs(interpolatedHtml));
            if (sanitizedHtml) {
              renderedAnswer = <Answer dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
            }
          }

          return (
            // `open` on a native <details> is uncontrolled: the element mutates its own state when the
            // author clicks it, and React only reasserts the attribute when the computed value changes.
            // Keying purely by index would therefore let a manually expanded row keep its DOM node
            // through a drag-reorder, so the expansion would follow the list position rather than the
            // item the author opened. Including the question makes the key change on reorder, which
            // remounts the rows and re-derives `open` from props. Rows carry no stable id, so index
            // stays in the key to keep it unique when two questions read the same.
            // eslint-disable-next-line react/no-array-index-key
            <Row key={`${index}:${row.question}`} open={isLiteralTrue(row.openByDefault)}>
              {/* Question text is not a heading: <summary> has an implicit ARIA role of button whose
                  presentational-children requirement strips nested heading semantics (JAWS/TalkBack
                  do not expose them). Rebuild as APG accordion: ECOMM-12770. */}
              <summary>
                <span>{displayQuestion}</span>
              </summary>
              {renderedAnswer}
            </Row>
          );
        })}
      </Wrapper>
    );
  },
};
